// The SpacetimeDB world as a WorldSocket (session.ts): ClientSession talks to it through the same messages whatever is
// behind it. What the client sends (WorldClientMessage) becomes a reducer call; what it reads (WorldServerMessage) is
// either a message the module wrote for this connection (its outbox: hello, character list, zone changes, stats,
// inventory, chat, notices...) or one made here from the tables the client subscribes to for its instance, the way
// a world server would make them from its own state: snapshots every tick (Body), entity spawns and despawns (Entity, Body),
// chunks streamed around the player, and with them their tile entities
// and loot bags, and combat events. This is the only file that sees the generated bindings; their tagged notification
// payloads become the session's ordinary typed events here.
import { serverEvent, type ServerKindInit, type WorldClientMessage, type WorldServerMessage } from '../events';
import { GameConstants } from '../../../../shared/src/constants';
import { log } from '../log';
import { chunkOfPixel, chunkOfTile, chunkSize } from '../map';
import { arrivals, type Delivery, type WorldSocket } from '../session';
import { DbConnection } from '../../spacetimedb/world_bindings';
import type { Body, CombatEvent, Entity, PlacedEntity as PlacedEntityRow, Outbox as OutboxRow, TileEntity as TileEntityRow, Bag as BagRow, HealZone as HealZoneRow, Corpse as CorpseRow, Buff as BuffRow, WorldNotification } from '../../spacetimedb/world_bindings/types';

export interface StdbWorldOptions {
  /** The SpacetimeDB server ("https://maincloud.spacetimedb.com", or a local "http://127.0.0.1:3000"). */
  uri: string;
  /** The world's database name. */
  database: string;
}

type ServerKind = ServerKindInit;

/** Chunks kept around the player, and how many to stream in at once. */
const streamRadius = 2;
const chunksPerFlush = 6;

export class StdbWorldLink implements WorldSocket {
  private readonly inbox: WorldServerMessage[] = [];
  bytesReceived = 0;
  private ended = false;
  private conn: DbConnection | null = null;
  private connectionHex = '';
  private flushQueued = false;
  private readonly pendingOutbox: OutboxRow[] = [];
  private lastOutbox = 0n;

  // the zone the client is in (from the last zone change it was sent)
  private epoch = 0;
  private instance = 0;
  private widthTiles = 0;
  private heightTiles = 0;
  private selfEntity = 0;
  private startX = 0;
  private startY = 0;
  private zoneSub: { unsubscribe(): void } | null = null;
  private zoneReady = false;
  private tick = 0;
  private sentTick = -1;

  private readonly bodies = new Map<number, Body>();
  private readonly entities = new Map<number, Entity>();
  private readonly spawned = new Set<number>();
  private readonly placedEntities = new Map<string, PlacedEntityRow>();
  private readonly healZones = new Map<bigint, HealZoneRow>();
  private readonly corpses = new Map<number, CorpseRow>();
  private readonly buffs = new Map<bigint, BuffRow>();
  private readonly tileEntities = new Map<number, TileEntityRow>();
  private readonly bags = new Map<number, BagRow>();
  private readonly sentChunks = new Set<string>();

  // ping: the session pings once a second; it's answered after the last reducer round trip
  private roundTrip = 0;
  private lastInputSent = 0;

  private constructor() {}

  /** Connects; resolves once subscribed (the session then sends its first message, which says hello). */
  static connect(options: StdbWorldOptions, token: string | undefined, onToken: (token: string) => void): Promise<StdbWorldLink> {
    const link = new StdbWorldLink();
    return new Promise((resolve, reject) => {
      let settled = false;
      const fail = (message: string) => {
        if (!settled) { settled = true; reject(new Error(message)); }
        link.end();
      };
      link.conn = DbConnection.builder().withUri(options.uri).withDatabaseName(options.database).withToken(token).withCompression('gzip')
        .onConnect((ctx, identity, newToken) => {
          onToken(newToken);
          link.connectionHex = ctx.connectionId.toHexString();
          log.info(`Connected to SpacetimeDB world '${options.database}' as ${identity.toHexString().slice(0, 16)}...`);
          link.listen(ctx.db);
          ctx.subscriptionBuilder()
            .onApplied(() => { if (!settled) { settled = true; resolve(link); } })
            .onError(() => fail('Could not subscribe to the world.'))
            .subscribe(['SELECT * FROM my_outbox', 'SELECT * FROM clock', 'SELECT * FROM visible_bag']);
        })
        .onConnectError((_ctx, e) => fail(`Could not reach the world (${(e as Error)?.message ?? 'refused'}).`))
        .onDisconnect(() => fail('Lost the connection to the world.'))
        .build();
    });
  }

  // =================================================================================================================
  // WorldSocket

  get isClosed(): boolean { return this.ended && this.inbox.length === 0; }

  tryReceive(): WorldServerMessage | null { return this.inbox.shift() ?? null; }

  close(): void {
    if (this.ended) return;
    this.end();
  }

  abort(): void { this.close(); }

  private end(): void {
    if (this.ended) return;
    this.ended = true;
    this.zoneSub = null;
    try { this.conn?.disconnect(); } catch { /* already closed */ }
    this.conn = null;
  }

  private push(kind: ServerKind): void { this.inbox.push(serverEvent(kind)); }

  /** A reducer call; its round trip is the ping the session shows. */
  private call(run: () => Promise<void>): void {
    const sent = performance.now();
    run().then(() => { this.roundTrip = performance.now() - sent; }, e => log.warn(`World request failed: ${(e as Error)?.message ?? e}`));
  }

  send(request: WorldClientMessage, _delivery?: Delivery): void {
    const r = this.conn?.reducers;
    if (!r || this.ended) return;
    const k = request.kind;
    switch (k.case) {
      case 'connect': this.call(() => r.hello({ protocol: k.value.protocol, contentFingerprint: k.value.contentHash })); break;
      case 'ping': {
        const token = k.value.token;
        setTimeout(() => { if (!this.ended) this.push({ case: 'pong', value: { token } }); }, Math.min(2000, this.roundTrip));
        break;
      }
      case 'createCharacter': this.call(() => r.createCharacter({ name: k.value.name, appearance: k.value.appearance })); break;
      case 'selectCharacter': this.call(() => r.selectCharacter({ name: k.value.name })); break;
      case 'input': {
        // Reliable and ordered here: only inputs not yet sent need to go (at least the newest).
        const v = k.value;
        const fresh = Math.max(1, Math.min(v.buttons.length, v.newestSequence - this.lastInputSent));
        this.lastInputSent = v.newestSequence;
        this.call(() => r.input({ epoch: v.epoch, newestSequence: v.newestSequence, buttons: v.buttons.slice(0, fresh) }));
        break;
      }
      case 'gatherEntity': this.call(() => r.gatherEntity({ epoch: k.value.epoch, placementId: k.value.placementId })); break;
      case 'mapRequest': this.call(() => r.mapRequest({ epoch: k.value.epoch })); break;
      case 'chatSend': this.call(() => r.chatSend({ text: k.value.text })); break;
      case 'inventoryMove': this.call(() => r.inventoryMove({ ...k.value })); break;
      case 'inventoryQuickMove': this.call(() => r.inventoryQuickMove({ container: k.value.container, index: k.value.index })); break;
      case 'destroyItem': this.call(() => r.destroyItem({ container: k.value.container, index: k.value.index, item: k.value.item })); break;
      case 'useItem': this.call(() => r.useItem({ container: k.value.container, index: k.value.index, item: k.value.item })); break;
      case 'dropItem': this.call(() => r.dropItem({ container: k.value.container, index: k.value.index, count: k.value.count })); break;
      case 'interactStart': this.call(() => r.interactStart({ epoch: k.value.epoch, x: k.value.x, y: k.value.y })); break;
      case 'interactCancel': this.call(() => r.interactCancel({ epoch: k.value.epoch })); break;
      case 'releaseCorpse': this.call(() => r.releaseCorpse({ epoch: k.value.epoch })); break;
      case 'rekindle': this.call(() => r.rekindle({ epoch: k.value.epoch })); break;
      case 'craft': this.call(() => r.craft({ recipe: k.value.recipe, count: k.value.count, station: k.value.station })); break;
      case 'bagTake': this.call(() => r.bagTake({ bag: k.value.bag, slot: k.value.slot, expectedItem: k.value.expectedItem })); break;
      case 'bagTakeAll': this.call(() => r.bagTakeAll({ bag: k.value.bag })); break;
      case 'bagDeposit': this.call(() => r.bagDeposit({ bag: k.value.bag, container: k.value.container, index: k.value.index })); break;
      case 'attack': this.call(() => r.attack({ epoch: k.value.epoch, angle: k.value.angle, viewTick: k.value.viewTick, distance: k.value.distance })); break;
      case 'block': this.call(() => r.block({ epoch: k.value.epoch, on: k.value.on })); break;
      case 'adminCommand': this.call(() => r.adminCommand({ action: k.value.action, arg: k.value.arg, count: k.value.count, x: k.value.x, y: k.value.y, name: k.value.name })); break;
      case 'partyAction': this.call(() => r.partyAction({ action: k.value.action, name: k.value.name })); break;
      case 'ignoreAction': this.call(() => r.ignoreAction({ ignore: k.value.ignore, name: k.value.name })); break;
      case 'friendAction': this.call(() => r.friendAction({ add: k.value.add, name: k.value.name })); break;
      case 'talkNpc': this.call(() => r.talkNpc({ epoch: k.value.epoch, tileEntity: k.value.tileEntity })); break;
      case 'questAction': this.call(() => r.questAction({ quest: k.value.quest, action: k.value.action })); break;
      case 'usePortal': this.call(() => r.usePortal({ epoch: k.value.epoch, tileEntity: k.value.tileEntity })); break;
      case 'talentAction': this.call(() => r.talentAction({ node: k.value.node, refund: k.value.refund })); break;
      case 'vendorAction': {
        const v = k.value;
        this.call(() => r.vendorAction({ epoch: v.epoch, npc: v.npc, action: v.action, container: v.container, index: v.index, item: v.item, count: v.count }));
        break;
      }
      case 'tradeAction': {
        const v = k.value;
        const slots = (v.offer?.slots ?? []).map(s => ({ slot: s.slot, item: s.item, count: s.count }));
        this.call(() => r.tradeAction({ action: v.action, name: v.name, slots, copper: v.offer?.copper ?? 0n, revision: v.revision }));
        break;
      }
      default: log.warn('Unknown request not sent to the SpacetimeDB world');
    }
  }

  // =================================================================================================================
  // what arrives

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private listen(db: any): void {
    const live = () => !this.ended;
    db.myOutbox.onInsert((_c: unknown, row: OutboxRow) => {
      if (!live() || row.connection.toHexString() !== this.connectionHex || row.id <= this.lastOutbox) return;
      this.pendingOutbox.push(row);
      this.queueFlush();
    });
    db.clock.onUpdate((_c: unknown, _o: unknown, row: { tick: bigint }) => { this.tick = Number(row.tick); this.queueFlush(); });
    db.clock.onInsert((_c: unknown, row: { tick: bigint }) => { this.tick = Number(row.tick); });

    db.body.onInsert((_c: unknown, row: Body) => this.onBody(row));
    db.body.onUpdate((_c: unknown, _o: Body, row: Body) => this.onBody(row));
    db.body.onDelete((_c: unknown, row: Body) => {
      if (row.instance !== this.instance) return;
      this.bodies.delete(row.entityId);
      this.despawn(row.entityId);
    });
    db.entity.onInsert((_c: unknown, row: Entity) => this.onEntity(row));
    db.entity.onUpdate((_c: unknown, old: Entity, row: Entity) => {
      this.onEntity(row);
      if (this.zoneReady && this.spawned.has(row.entityId) && row.instance === this.instance && old.armor.join() !== row.armor.join())
        this.push({ case: 'entityEquipment', value: { epoch: this.epoch, entityId: row.entityId, armor: row.armor } });
    });
    db.entity.onDelete((_c: unknown, row: Entity) => {
      if (row.instance !== this.instance) return;
      this.entities.delete(row.entityId);
      this.despawn(row.entityId);
    });

    const placed = (row: PlacedEntityRow) => {
      if (row.instance !== this.instance) return;
      this.placedEntities.set(row.placementId, row);
      if (this.zoneReady) this.pushPlacedEntity(row);
    };
    db.placedEntity.onInsert((_c: unknown, row: PlacedEntityRow) => placed(row));
    db.placedEntity.onUpdate((_c: unknown, _old: PlacedEntityRow, row: PlacedEntityRow) => placed(row));
    db.placedEntity.onDelete((_c: unknown, row: PlacedEntityRow) => {
      if (row.instance !== this.instance || !this.placedEntities.delete(row.placementId)) return;
      if (this.zoneReady) this.push({ case: "placedEntityState", value: { epoch: this.epoch, id: row.placementId, removed: true } });
    });
    db.healZone.onInsert((_c: unknown, row: HealZoneRow) => {
      if (row.instance !== this.instance) return;
      this.healZones.set(row.id, row);
      if (this.zoneReady) this.pushHealZone(row);
    });
    db.healZone.onDelete((_c: unknown, row: HealZoneRow) => {
      if (row.instance !== this.instance || !this.healZones.delete(row.id)) return;
      if (this.zoneReady) this.pushHealZone(row, true);
    });
    db.corpse.onInsert((_c: unknown, row: CorpseRow) => {
      if (row.instance !== this.instance) return;
      this.corpses.set(row.entityId, row);
      if (this.zoneReady) this.pushCorpse(row);
    });
    db.corpse.onDelete((_c: unknown, row: CorpseRow) => {
      if (row.instance !== this.instance || !this.corpses.delete(row.entityId)) return;
      if (this.zoneReady) this.pushCorpse(row, true);
    });
    const buffChanged = (row: BuffRow) => {
      if (row.instance !== this.instance) { if (this.buffs.delete(row.id) && this.zoneReady) this.pushBuff(row, true); return; }
      this.buffs.set(row.id, row);
      if (this.zoneReady) this.pushBuff(row);
    };
    db.buff.onInsert((_c: unknown, row: BuffRow) => buffChanged(row));
    db.buff.onUpdate((_c: unknown, _old: BuffRow, row: BuffRow) => buffChanged(row));
    db.buff.onDelete((_c: unknown, row: BuffRow) => {
      if (!this.buffs.delete(row.id)) return;
      if (this.zoneReady) this.pushBuff(row, true);
    });
    db.tileEntity.onInsert((_c: unknown, row: TileEntityRow) => {
      if (row.instance !== this.instance) return;
      this.tileEntities.set(row.id, row);
      if (this.zoneReady && this.sentChunks.has(this.chunkOfTileKey(row.x, row.y))) this.pushTileEntity(row);
    });
    db.tileEntity.onDelete((_c: unknown, row: TileEntityRow) => {
      if (row.instance !== this.instance || !this.tileEntities.delete(row.id)) return;
      if (this.zoneReady) this.push({ case: 'tileEntityDespawn', value: { epoch: this.epoch, id: row.id } });
    });

    db.combatEvent.onInsert((_c: unknown, e: CombatEvent) => {
      if (!this.zoneReady || e.instance !== this.instance || e.tick + 30 < this.tick) return;
      if (e.except !== 0 && e.except === this.selfEntity) return;
      this.push({ case: 'combatEvent', value: { epoch: this.epoch, type: e.type, entity: e.entity, other: e.other, value: e.value } });
    });

    const bag = (row: BagRow) => {
      if (row.instance !== this.instance) return;
      this.bags.set(row.id, row);
      if (this.zoneReady && this.sentChunks.has(this.chunkOfPixelKey(row.x, row.y))) this.pushBag(row);
    };
    db.visibleBag.onInsert((_c: unknown, row: BagRow) => bag(row));
    db.visibleBag.onUpdate((_c: unknown, _o: BagRow, row: BagRow) => bag(row));
    db.visibleBag.onDelete((_c: unknown, row: BagRow) => {
      if (!this.bags.delete(row.id) || row.instance !== this.instance) return;
      if (this.zoneReady && this.sentChunks.has(this.chunkOfPixelKey(row.x, row.y)))
        this.push({ case: 'lootBagDespawn', value: { epoch: this.epoch, id: row.id, reason: 0 } });
    });
  }

  private onBody(row: Body): void {
    if (row.instance !== this.instance) return;
    this.bodies.set(row.entityId, row);
    this.trySpawn(row.entityId);
    this.queueFlush();
  }

  private onEntity(row: Entity): void {
    if (row.instance !== this.instance) return;
    this.entities.set(row.entityId, row);
    this.trySpawn(row.entityId);
  }

  private trySpawn(id: number): void {
    if (!this.zoneReady || id === this.selfEntity || this.spawned.has(id)) return;
    const b = this.bodies.get(id), e = this.entities.get(id);
    if (!b || !e) return;
    this.spawned.add(id);
    this.push({
      case: 'entitySpawn',
      value: {
        epoch: this.epoch, entityId: id, kind: e.kind, name: e.name, appearance: e.appearance, level: e.level, armor: e.armor,
        x: b.x, y: b.y, facing: b.facingFlags & 3, flags: b.facingFlags >>> 2,
      },
    });
  }

  private despawn(id: number): void {
    if (this.spawned.delete(id) && this.zoneReady) this.push({ case: 'entityDespawn', value: { epoch: this.epoch, entityId: id } });
  }

  private queueFlush(): void {
    if (this.flushQueued) return;
    this.flushQueued = true;
    queueMicrotask(() => { this.flushQueued = false; this.flush(); });
  }

  /** After a transaction's rows: its messages in order, then what changed in the world (streaming, the snapshot). */
  private flush(): void {
    if (this.ended) return;
    arrivals.onArrival?.();
    if (this.pendingOutbox.length > 0) {
      const rows = this.pendingOutbox.splice(0).sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));
      for (const row of rows) {
        if (row.id <= this.lastOutbox) continue;
        this.lastOutbox = row.id;
        this.deliver(row.notification);
      }
    }
    if (!this.zoneReady) return;
    this.stream();
    if (this.tick !== this.sentTick) {
      this.sentTick = this.tick;
      this.snapshot();
    }
  }

  /** A notification the module wrote for us: passed on as an event, after noting what it changes here. */
  private deliver(notification: WorldNotification): void {
    // Generated tags are PascalCase ("ZoneChange"); the session's event cases are camelCase ("zoneChange").
    const name = notification.tag[0].toLowerCase() + notification.tag.slice(1);
    const k = { case: name, value: notification.value } as ServerKind;
    if (k.case === 'contentData') this.bytesReceived += k.value.bundle?.length ?? 0;
    if (k.case === 'mapData') this.bytesReceived += k.value.terrain?.length ?? 0;
    if (k.case === 'welcome') this.selfEntity = k.value.entityId ?? 0;
    this.inbox.push(serverEvent(k));
    if (k.case === 'zoneChange') this.enterZone(k.value.epoch ?? 0, k.value.instanceId ?? 0, k.value.widthTiles ?? 0, k.value.heightTiles ?? 0, k.value.x ?? 0, k.value.y ?? 0);
    if (k.case === 'disconnect') this.end();
  }

  /** A new zone: forget the old one's rows and subscribe to the new instance's. Chunks follow once its rows are in. */
  private enterZone(epoch: number, instance: number, width: number, height: number, x: number, y: number): void {
    this.epoch = epoch;
    this.startX = x;
    this.startY = y;
    this.widthTiles = width;
    this.heightTiles = height;
    this.sentChunks.clear();
    this.spawned.clear();
    if (instance === this.instance && this.zoneSub) {
      // The same instance (a teleport): its rows are current; the session has forgotten what it was shown.
      if (this.zoneReady) {
        for (const row of this.placedEntities.values()) this.pushPlacedEntity(row);
        for (const row of this.healZones.values()) this.pushHealZone(row);
        for (const row of this.corpses.values()) this.pushCorpse(row);
        for (const row of this.buffs.values()) this.pushBuff(row);
        for (const id of this.bodies.keys()) this.trySpawn(id);
      }
      return;
    }
    this.zoneReady = false;
    this.instance = instance;
    this.bodies.clear();
    this.entities.clear();
    this.tileEntities.clear();
    this.placedEntities.clear();
    this.healZones.clear();
    this.corpses.clear();
    this.buffs.clear();
    for (const [id, b] of this.bags) if (b.instance !== instance) this.bags.delete(id);
    try { this.zoneSub?.unsubscribe(); } catch { /* already gone */ }
    const conn = this.conn;
    if (!conn) return;
    const where = `WHERE instance = ${instance >>> 0}`;
    const sub = conn.subscriptionBuilder().onApplied(() => {
      if (this.zoneSub !== sub || this.instance !== instance) return;
      // Rows may arrive before or after the applied callback: take what the cache has now.
      const db = conn.db;
      for (const r of db.body.iter()) if (r.instance === instance) this.bodies.set(r.entityId, r);
      for (const r of db.entity.iter()) if (r.instance === instance) this.entities.set(r.entityId, r);
      for (const r of db.tileEntity.iter()) if (r.instance === instance) this.tileEntities.set(r.id, r);
      for (const r of db.visibleBag.iter()) if (r.instance === instance) this.bags.set(r.id, r);
      for (const r of db.placedEntity.iter()) if (r.instance === instance) this.placedEntities.set(r.placementId, r);
      for (const r of db.healZone.iter()) if (r.instance === instance) this.healZones.set(r.id, r);
      for (const r of db.corpse.iter()) if (r.instance === instance) this.corpses.set(r.entityId, r);
      for (const r of db.buff.iter()) if (r.instance === instance) this.buffs.set(r.id, r);
      this.zoneReady = true;
      for (const row of this.placedEntities.values()) this.pushPlacedEntity(row);
      for (const row of this.healZones.values()) this.pushHealZone(row);
      for (const row of this.corpses.values()) this.pushCorpse(row);
      for (const row of this.buffs.values()) this.pushBuff(row);
      for (const id of this.bodies.keys()) this.trySpawn(id);
      this.flush();
    }).onError(() => { log.error('Could not subscribe to the zone'); this.end(); })
      .subscribe([`SELECT * FROM body ${where}`, `SELECT * FROM entity ${where}`, `SELECT * FROM tile_entity ${where}`,
        `SELECT * FROM placed_entity ${where}`, `SELECT * FROM combat_event ${where}`, `SELECT * FROM heal_zone ${where}`,
        `SELECT * FROM corpse ${where}`, `SELECT * FROM buff ${where}`]);
    this.zoneSub = sub;
  }

  private pushPlacedEntity(row: PlacedEntityRow): void {
    this.push({ case: "placedEntityState", value: { epoch: this.epoch, id: row.placementId,
      definition: row.definition, x: row.x, y: row.y, offsetX: row.offsetX, offsetY: row.offsetY, depleted: row.depleted } });
  }

  private pushHealZone(row: HealZoneRow, removed = false): void {
    this.push({ case: 'healZoneState', value: { epoch: this.epoch, id: Number(row.id), x: row.x, y: row.y, item: row.item,
      startTick: row.startTick, endTick: row.endTick, removed } });
  }

  private pushCorpse(row: CorpseRow, removed = false): void {
    this.push({ case: 'corpseState', value: { epoch: this.epoch, entityId: row.entityId, x: row.x, y: row.y,
      appearance: row.appearance, facing: row.facing, removed } });
  }

  private pushBuff(row: BuffRow, removed = false): void {
    this.push({ case: 'buffState', value: { epoch: this.epoch, id: Number(row.id), entityId: row.entityId, kind: row.kind,
      startTick: row.startTick, endTick: row.endTick, removed } });
  }

  private chunkOfTileKey(x: number, y: number): string { return `${chunkOfTile(x)},${chunkOfTile(y)}`; }
  private chunkOfPixelKey(x: number, y: number): string { return `${chunkOfPixel(x)},${chunkOfPixel(y)}`; }

  /** Chunks into view around the player, nearest first, with what's changed on them and what travels with them. */
  private stream(): void {
    const self = this.bodies.get(this.selfEntity);
    const px = (self && self.epoch === (this.epoch & 255) ? self.x : this.startX) / 256;
    const py = (self && self.epoch === (this.epoch & 255) ? self.y : this.startY) / 256;
    const cx = chunkOfPixel(px), cy = chunkOfPixel(py);
    for (const key of [...this.sentChunks]) {
      const [x, y] = key.split(',').map(Number);
      if (Math.max(Math.abs(x - cx), Math.abs(y - cy)) <= streamRadius + 1) continue;
      this.sentChunks.delete(key);
      this.push({ case: 'chunkUnload', value: { epoch: this.epoch, x, y } });
    }
    const wide = Math.ceil(this.widthTiles / chunkSize), high = Math.ceil(this.heightTiles / chunkSize);
    let budget = chunksPerFlush;
    for (let r = 0; r <= streamRadius && budget > 0; r++)
      for (let dy = -r; dy <= r && budget > 0; dy++)
        for (let dx = -r; dx <= r && budget > 0; dx++) {
          if (Math.max(Math.abs(dx), Math.abs(dy)) !== r) continue;
          const x = cx + dx, y = cy + dy, key = `${x},${y}`;
          if (x < 0 || y < 0 || x >= wide || y >= high || this.sentChunks.has(key)) continue;
          this.sentChunks.add(key);
          budget--;
          this.push({ case: 'chunkData', value: { epoch: this.epoch, x, y, indices: [], objects: [] } });
          for (const e of this.tileEntities.values()) if (chunkOfTile(e.x) === x && chunkOfTile(e.y) === y) this.pushTileEntity(e);
          for (const b of this.bags.values()) if (chunkOfPixel(b.x) === x && chunkOfPixel(b.y) === y) this.pushBag(b);
        }
  }

  private pushTileEntity(e: TileEntityRow): void {
    this.push({ case: 'tileEntitySpawn', value: { epoch: this.epoch, id: e.id, type: e.type, x: e.x, y: e.y, data: e.data, label: e.label } });
  }

  private pushBag(b: BagRow): void {
    this.push({ case: 'lootBagSpawn', value: { epoch: this.epoch, id: b.id, x: b.x, y: b.y, contents: b.contents.map(s => ({ item: s.item, count: s.count, bound: s.bound })) } });
  }

  /** This tick's snapshot: our exact motor and acknowledged input, and where everyone we can see is. */
  private snapshot(): void {
    const self = this.bodies.get(this.selfEntity);
    const mine = self && self.epoch === (this.epoch & 255) ? self : undefined;
    const entities = [];
    for (const id of this.spawned) {
      const b = this.bodies.get(id);
      if (b) entities.push({ entityId: id, x: b.x, y: b.y, facingFlags: b.facingFlags, healthPct: b.healthPct });
    }
    this.push({
      case: 'snapshot',
      value: {
        epoch: this.epoch, tick: this.tick >>> 0, lastProcessedInput: mine?.ack ?? 0, entities,
        self: mine ? { entityId: mine.entityId, x: mine.x, y: mine.y, facingFlags: mine.facingFlags, stamina: mine.stamina, staminaDelay: mine.staminaDelay, stun: mine.stun } : undefined,
      },
    });
  }
}

/** For the bots and tests: the protocol version the module expects. */
export const stdbProtocol = GameConstants.protocolVersion;
